import React, { useState, useCallback, useEffect, type RefObject } from 'react';
import type { AreaBoundary, RowBoundary, ViewState, Place, PortBoundary } from '../types/warehouse.types';
import { isPointInPolygon, lineToRectanglePolygon } from '../utils/geometry';
import { findPlaceAtPosition } from '../utils/placeUtils';
import { screenToWorld } from '../utils/coordinateTransform';
import type { BufferedCanvasCache } from '../renderers/core/BufferedCanvasCache';

const ZERO_PAN_SHIFT = { deltaX: 0, deltaY: 0 };

interface UseAreaRowHoverProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  portBoundaries: PortBoundary[];
  areaBoundaries: AreaBoundary[];
  rowBoundaries: RowBoundary[];
  viewState: ViewState;
  places: Map<string, Place>;
  bufferedCacheRef?: React.MutableRefObject<BufferedCanvasCache | null>;
}

interface UseAreaRowHoverResult {
  hoveredPort: PortBoundary | null;
  hoveredArea: AreaBoundary | null;
  hoveredRow: RowBoundary | null;
  hoveredPlace: Place | null;
  mousePosition: { x: number; y: number } | null;
}

/**
 * Hook to detect when mouse is hovering over Areas or Rows
 */
export const useAreaRowHover = ({
  canvasRef,
  portBoundaries,
  areaBoundaries,
  rowBoundaries,
  viewState,
  places,
  bufferedCacheRef
}: UseAreaRowHoverProps): UseAreaRowHoverResult => {
  const [hoveredPort, setHoveredPort] = useState<PortBoundary | null>(null);
  const [hoveredArea, setHoveredArea] = useState<AreaBoundary | null>(null);
  const [hoveredRow, setHoveredRow] = useState<RowBoundary | null>(null);
  const [hoveredPlace, setHoveredPlace] = useState<Place | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

  /**
   * Handle mouse move to detect hover with proper priority handling
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    setMousePosition({ x: screenX, y: screenY });

    const panShift = bufferedCacheRef?.current?.getPanShift() || ZERO_PAN_SHIFT;
    const correctedScreenX = screenX - panShift.deltaX;
    const correctedScreenY = screenY - panShift.deltaY;

    const worldPos = screenToWorld(screenX, screenY, viewState, canvas.width, canvas.height);

    // Priority 1: Check Places
    const foundPlace = findPlaceAtPosition(places, correctedScreenX, correctedScreenY);
    setHoveredPlace(foundPlace);

    if (foundPlace) {
      // Clear all lower priority items
      setHoveredRow(null);
      setHoveredArea(null);
      setHoveredPort(null);
      return;
    }

    // Priority 2: Check Rows
    let foundRow: RowBoundary | null = null;
    for (const row of rowBoundaries) {
      if (isPointInPolygon(worldPos, row.polygon)) {
        foundRow = row;
        break;
      }
    }
    setHoveredRow(foundRow);
    
    if (foundRow) {
      // Clear all lower priority items
      setHoveredArea(null);
      setHoveredPort(null);
      return;
    }

    // Priority 3: Check Areas
    let foundArea: AreaBoundary | null = null;
    for (const area of areaBoundaries) {
      if (isPointInPolygon(worldPos, area.polygon)) {
        foundArea = area;
        break;
      }
    }
    setHoveredArea(foundArea);
    
    if (foundArea) {
      // Clear lower priority items
      setHoveredPort(null);
      return;
    }

    // Priority 4: Check Ports (lowest priority)
    // Ports are defined as lines (2 points), convert to rectangles for hit detection
    let foundPort: PortBoundary | null = null;
    const portWidth = 15 / viewState.scale; // Adaptive width based on zoom level
    
    for (const port of portBoundaries) {
      if (port.polygon.length === 2) {
        if (!port.hitBox) {
          // Port is a line - convert to rectangle
            port.hitBox = lineToRectanglePolygon(
              port.polygon[0],
              port.polygon[1],
              portWidth
            );
          }
        
        if (port.hitBox.length > 0 && isPointInPolygon(worldPos, port.hitBox)) {
          foundPort = port;
          break;
        }
      } else if (port.polygon.length >= 3) {
        // Port is already a polygon (fallback case)
        if (isPointInPolygon(worldPos, port.polygon)) {
          foundPort = port;
          break;
        }
      }
    }
    setHoveredPort(foundPort);

  }, [canvasRef, portBoundaries, areaBoundaries, rowBoundaries, places, viewState, bufferedCacheRef]);

  /**
   * Handle mouse leave to clear hover
   */
  const handleMouseLeave = useCallback(() => {
    setHoveredPort(null);
    setHoveredArea(null);
    setHoveredRow(null);
    setHoveredPlace(null);
    setMousePosition(null);
  }, []);

  /**
   * Attach event listeners
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [canvasRef, handleMouseMove, handleMouseLeave]);

  return {
    hoveredPort,
    hoveredArea,
    hoveredRow,
    hoveredPlace,
    mousePosition
  };
};